// SPDX-License-Identifier: AGPL-3.0-or-later

import {Int32Type} from '@fluxer/schema/src/primitives/SchemaPrimitives';
import {z} from 'zod';

const MAX_COUNTRY_ROUTES = 250;

export const ShopPaymentProvider = z.enum(['payriff', 'paddle']).describe('Who takes the payment');

export type ShopPaymentProvider = z.infer<typeof ShopPaymentProvider>;

export const ShopChargeCurrency = z
	.enum(['AZN', 'USD', 'TRY'])
	.describe('The currency the provider charges: AZN for Payriff, USD or TRY for Paddle');

export type ShopChargeCurrency = z.infer<typeof ShopChargeCurrency>;

export const ShopPayable = z
	.object({
		amount_minor: Int32Type.describe('In the minor unit of currency: cents, kuruş or qəpik'),
		currency: ShopChargeCurrency,
		try_minor: Int32Type.optional().describe(
			'Payriff shown in TL only: the lira price in kuruş that this manat amount is the CBAR value of; show it',
		),
	})
	.describe('An amount the server prices for this viewer: its payment route, currency and discounts');

export type ShopPayable = z.infer<typeof ShopPayable>;

// What a price object charges this viewer, in its provider's currency; the US cent fields beside them stay as they were.
export const ShopPayableFields = {
	payable: ShopPayable.nullable()
		.optional()
		.describe('What this viewer pays now; null: nobody sells to this viewer, or the amount cannot be charged'),
	list_payable: ShopPayable.nullable()
		.optional()
		.describe('The list price without a campaign or the member discount, as charged to this viewer'),
	plus_payable: ShopPayable.nullable()
		.optional()
		.describe(
			'Items and bundles: what this viewer would pay with the paid premium member discount; null when it gets it already or there is none',
		),
};

const PaddleRoute = z.object({
	provider: z.literal('paddle'),
	currency: z.enum(['USD', 'TRY']).describe('Charged by Paddle in this currency'),
});
const PayriffRoute = z.object({
	provider: z.literal('payriff'),
	currency: z
		.enum(['TRY', 'AZN'])
		.describe('Shown in this currency; Payriff always charges AZN. TRY falls back to AZN without a TL rate'),
});

export const ShopPaymentRoute = z
	.discriminatedUnion('provider', [PaddleRoute, PayriffRoute, z.object({provider: z.literal('off')})])
	.describe('Who sells to a country and in which currency; off closes the shop there');

export type ShopPaymentRoute = z.infer<typeof ShopPaymentRoute>;

export const ShopPaymentCountryCode = z
	.string()
	.regex(/^[A-Z]{2}$/)
	.describe('ISO 3166-1 alpha-2 country code, upper case, as GeoIP reports it');

export const ShopPaymentCountryRoutes = z
	.record(ShopPaymentCountryCode, ShopPaymentRoute)
	.refine((routes) => Object.keys(routes).length <= MAX_COUNTRY_ROUTES, `At most ${MAX_COUNTRY_ROUTES} countries`)
	.describe('Country → route; a country missing here, or an unknown country, takes the default route');

export type ShopPaymentCountryRoutes = z.infer<typeof ShopPaymentCountryRoutes>;

export const ShopPaymentProviderSwitches = z
	.object({
		payriff: z.object({orders_open: z.boolean()}),
		paddle: z.object({
			orders_open: z.boolean(),
			subscriptions_open: z.boolean().optional(),
			username_cards_open: z.boolean().optional().describe('Paddle sells username cards; absent: closed'),
		}),
	})
	.describe('Admin switches for new orders per provider. A closed provider still checks and delivers its open orders');

export type ShopPaymentProviderSwitches = z.infer<typeof ShopPaymentProviderSwitches>;

export const ShopViewerPayment = z
	.discriminatedUnion('provider', [
		PaddleRoute.extend({
			min_charge_minor: Int32Type.optional().describe(
				'Paddle refuses a smaller charge, in the minor unit of currency: a price under it is not sold here',
			),
		}),
		PayriffRoute,
	])
	.describe('How this viewer pays: the provider and the currency prices are shown in');

export type ShopViewerPayment = z.infer<typeof ShopViewerPayment>;

export const ShopPaymentRouteWarning = z.object({
	country: ShopPaymentCountryCode.nullable().describe('null for the default route, and for geoip_unavailable'),
	provider: ShopPaymentProvider.optional().describe('absent for geoip_unavailable'),
	reason: z
		.enum(['not_configured', 'orders_closed', 'try_rate_missing', 'geoip_unavailable'])
		.describe(
			'Why this route sells nothing now: it falls to off; geoip_unavailable: no country is known, so every buyer takes the default route',
		),
});

export type ShopPaymentRouteWarning = z.infer<typeof ShopPaymentRouteWarning>;

export const SHOP_DEFAULT_PAYMENT_ROUTE: ShopPaymentRoute = {provider: 'payriff', currency: 'TRY'};

export const SHOP_DEFAULT_PAYMENT_PROVIDERS: ShopPaymentProviderSwitches = {
	payriff: {orders_open: true},
	paddle: {orders_open: false, subscriptions_open: false, username_cards_open: false},
};
